export const BROADER_JOURNEY_IDS = Object.freeze([
  'kch-planned-inpatient-surgery',
  'kch-planned-inpatient-medical',
  'kch-inpatient-ward',
  'kch-childrens-services',
  'kch-cancer-care',
]);

const COMPLETION = Object.freeze({
  compact: true,
  label: 'Journey summary',
  title: "You've reached the end of this guide",
  description: 'You do not need to open everything. Use the parts that are helpful to you and return whenever you need them.',
});

const QUALITY_BY_JOURNEY_ID = Object.freeze({
  'kch-planned-inpatient-surgery': {
    stages: [
      {
        id: 'before-admission',
        title: 'Before admission',
        description: 'Prepare for your admission, medicines, fasting and what to bring with you.',
        phraseKeys: ['preAssessment', 'nilByMouth', 'consent'],
        linkIds: ['admission', 'preparing'],
        questionIndexes: [0, 1],
      },
      {
        id: 'surgery-and-ward-recovery',
        title: 'Surgery and ward recovery',
        description: 'Understand who may be involved, how pain and recovery may be supported, and what to expect on the ward.',
        phraseKeys: ['cannula', 'theatre', 'recovery', 'analgesia', 'antiemetic', 'mobilise', 'vtePrevention'],
        linkIds: ['ward'],
        questionIndexes: [2, 3, 4],
      },
      {
        id: 'planning-to-go-home',
        title: 'Planning to go home',
        description: 'Clarify discharge, support at home and who to contact if you have concerns afterwards.',
        phraseKeys: ['medicallyFitForDischarge', 'takeHomeMedicines', 'followUp', 'safetyNetting'],
        linkIds: ['discharge'],
        questionIndexes: [5, 6],
      },
    ],
  },
  'kch-planned-inpatient-medical': {
    stages: [
      {
        id: 'before-admission',
        title: 'Before admission',
        description: 'Prepare for arrival, medicines and what to bring for your planned stay.',
        phraseKeys: ['admission'],
        linkIds: ['admission', 'medicines'],
        questionIndexes: [0, 2],
      },
      {
        id: 'during-your-stay',
        title: 'During your stay',
        description: 'Understand the plan for tests or treatment, who is coordinating your care and how to maintain independence.',
        phraseKeys: ['observations', 'wardRound', 'handover', 'carePlan', 'bloodTests', 'mobilise'],
        linkIds: ['ward'],
        questionIndexes: [1, 3, 4],
      },
      {
        id: 'leaving-hospital',
        title: 'Leaving hospital',
        description: 'Understand discharge planning, follow-up and who to contact after you leave.',
        phraseKeys: ['medicallyFitForDischarge', 'dischargeSummary', 'takeHomeMedicines', 'followUp', 'safetyNetting'],
        linkIds: ['discharge'],
        questionIndexes: [5, 6],
      },
    ],
  },
  'kch-inpatient-ward': {
    stages: [
      {
        id: 'getting-oriented',
        title: 'Getting oriented',
        description: 'Understand the ward, who is responsible for your care and who to ask if you have questions.',
        phraseKeys: ['handover', 'wardRound', 'observations'],
        linkIds: ['ward', 'visiting'],
        questionIndexes: [0, 1, 2],
      },
      {
        id: 'during-your-stay',
        title: 'During your stay',
        description: 'Ask about medicines, mobility, support and the plan for your care each day.',
        phraseKeys: ['carePlan', 'review', 'prn', 'analgesia', 'mobilise', 'fluidBalance'],
        linkIds: ['support'],
        questionIndexes: [3, 4],
      },
      {
        id: 'planning-for-discharge',
        title: 'Planning for discharge',
        description: 'Find out what needs to happen before you leave and what support may be arranged afterwards.',
        phraseKeys: ['medicallyFitForDischarge', 'dischargeSummary', 'takeHomeMedicines', 'followUp', 'safetyNetting'],
        linkIds: ['discharge'],
        questionIndexes: [5, 6],
      },
    ],
  },
  'kch-childrens-services': {
    stages: [
      {
        id: 'before-you-come',
        title: 'Before you come',
        description: 'Prepare for the visit, find where to go and plan any extra support or adjustments.',
        phraseKeys: ['childrensOutpatientClinic'],
        linkIds: ['visit', 'where', 'support'],
        questionIndexes: [1, 3],
      },
      {
        id: 'during-the-visit',
        title: 'During the visit',
        description: 'Understand what will happen, who can explain things and what may help a child or young person feel more comfortable.',
        phraseKeys: ['paediatricAssessment', 'playSpecialist', 'familyCentredCare', 'paediatricObservationChart'],
        linkIds: ['family'],
        questionIndexes: [0, 2, 4, 5],
      },
      {
        id: 'after-you-go-home',
        title: 'After you go home',
        description: 'Know who the family should contact if there are questions or concerns afterwards.',
        phraseKeys: ['followUp', 'safetyNetting'],
        linkIds: [],
        questionIndexes: [6],
      },
    ],
  },
  'kch-cancer-care': {
    stages: [
      {
        id: 'understanding-your-team',
        title: 'Understanding your team',
        description: 'Know your main point of contact and which professionals may be involved in your care.',
        phraseKeys: ['oncology', 'clinicalNurseSpecialist', 'cancerMdt'],
        linkIds: ['team'],
        questionIndexes: [0, 1],
      },
      {
        id: 'appointments-and-treatment',
        title: 'Appointments and treatment',
        description: 'Prepare for appointments and treatment, and understand what the team is aiming to achieve and monitor.',
        phraseKeys: ['biopsy', 'treatmentCycle', 'cancerDayUnit', 'sideEffects', 'followUpScan'],
        linkIds: ['appointments'],
        questionIndexes: [2, 3, 5],
      },
      {
        id: 'support-and-urgent-concerns',
        title: 'Support and urgent concerns',
        description: 'Find practical and emotional support and know who to contact if you become unwell or worried.',
        phraseKeys: ['neutropenia', 'supportiveCare', 'safetyNetting'],
        linkIds: ['support', 'urgent'],
        questionIndexes: [4, 6],
      },
    ],
  },
});

function withRole(roles = [], role) {
  if (!role?.id || roles.some((item) => item?.id === role.id)) return roles;
  return [role, ...roles];
}

// Canonical English patient-facing stage overrides; clinical inventory consumes this unchanged.
export const BROADER_JOURNEY_QUALITY = QUALITY_BY_JOURNEY_ID;

export function applyBroaderJourneyQuality(journey) {
  if (!journey) return journey;
  const quality = QUALITY_BY_JOURNEY_ID[journey.id];
  if (!quality) return journey;

  const roles = journey.id === 'kch-planned-inpatient-surgery'
    ? withRole(journey.roles, { id: 'pre_assessment_nurse', title: 'Pre-assessment Nurse' })
    : journey.roles;

  return {
    ...journey,
    completion: journey.completion || COMPLETION,
    stages: quality.stages,
    roles,
  };
}
